import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  NgControl,
  NgForm,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ObjectName } from '../../../models/view-model';
import { ObjectTableComponent } from '../object-table/object-table.component';
import { CloseButtonComponent } from '@shared/components/close-button/close-button.component';

// TODO disabled support
@Component({
  selector: 'app-table-input',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    ObjectTableComponent,
    CloseButtonComponent,
  ],
  templateUrl: './table-input.component.html',
  providers: [{ provide: MatFormFieldControl, useExisting: TableInputComponent }],
  styleUrls: ['./table-input.component.scss'],
  // tslint:disable-next-line:use-host-property-decorator
  host: {
    '[id]': 'id',
    '[attr.aria-describedby]': 'describedBy',
  },
})
export class TableInputComponent
  implements MatFormFieldControl<any>, ControlValueAccessor, DoCheck, OnDestroy
{
  ngControl = inject(NgControl, { optional: true, self: true });
  private fm = inject(FocusMonitor);
  private elRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private _parentForm = inject(NgForm, { optional: true });
  private _parentFormGroup = inject(FormGroupDirective, { optional: true });
  private _defaultErrorStateMatcher = inject(ErrorStateMatcher);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);

  static nextId = 0;

  @Input()
  filterObjectName!: ObjectName;

  @Input()
  valueTemplate!: TemplateRef<any>;

  selectedIds: any[] = [];

  readonly autofilled: boolean = false;
  focused = false;
  errorState = false;
  controlType = 'app-table-input';
  id = `app-table-input-${TableInputComponent.nextId++}`;
  readonly stateChanges = new Subject<void>();
  describedBy = '';
  errorStateMatcher: ErrorStateMatcher | null = null;

  @Input()
  multi = false;

  get empty(): boolean {
    return this.selectedIds.length === 0;
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  // tslint:disable-next-line:variable-name
  private _placeholder: string = '';

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  // tslint:disable-next-line:variable-name
  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  // tslint:disable-next-line:variable-name
  private _disabled = false;

  @Input()
  get value(): any | null {
    if (this.multi) {
      return this.selectedIds;
    } else {
      return this.selectedIds.length === 1 ? this.selectedIds[0] : undefined;
    }
  }
  set value(value: any | null) {
    if (Array.isArray(value)) {
      this.selectedIds = value ? value : [];
    } else {
      this.selectedIds = value ? [value] : [];
    }
    this.stateChanges.next();
    this.onChange(this.value);
  }

  @ViewChild('dialogTemplate', { static: false })
  dialogTemplate!: TemplateRef<any>;

  dialogRef?: MatDialogRef<any>;

  onChange = (value: any) => {};

  onTouched = () => {};

  constructor() {
    const fm = this.fm;
    const elRef = this.elRef;

    fm.monitor(elRef.nativeElement, true).subscribe((origin) => {
      const focused = !!origin;
      if (this.focused && !focused) {
        this.onTouched();
      }
      this.focused = focused;
      this.stateChanges.next();
    });

    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngDoCheck() {
    if (this.ngControl) {
      // We need to re-evaluate this on every change detection cycle, because there are some
      // error triggers that we can't subscribe to (e.g. parent form submissions). This means
      // that whatever logic is in here has to be super lean or we risk destroying the performance.
      this.updateErrorState();
    }
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
    this.dialogRef?.close();
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      // tslint:disable-next-line:no-non-null-assertion
      this.elRef.nativeElement.querySelector('input')!.focus();
    }
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj: any): void {
    this.value = obj;
  }

  // from angular/material2 src/lib/core/common-behaviors/error-state.ts
  updateErrorState(): void {
    const oldState = this.errorState;
    const parent = this._parentFormGroup || this._parentForm;
    const matcher = this.errorStateMatcher || this._defaultErrorStateMatcher;
    const control = this.ngControl ? (this.ngControl.control as FormControl) : null;
    const newState = matcher.isErrorState(control, parent);

    if (newState !== oldState) {
      this.errorState = newState;
      this.stateChanges.next();
    }
  }

  @HostListener('click')
  onClick(): void {
    this.openDialog();
  }

  onSelectedRowsChanged(ids: any[]): void {
    if (this.multi) {
      this.value = ids;
    } else {
      this.value = ids.length === 1 ? ids[0] : undefined;
    }
  }

  onSelectClick(): void {
    this.dialogRef?.close();
  }

  onCloseClick(): void {
    this.dialogRef?.close();
  }

  private openDialog(): void {
    this.dialogRef = this.dialog.open(this.dialogTemplate, { width: '100vw' });
  }

  getValueTemplateContext(id: any): any {
    const onRemove = (event: Event) => {
      if (event) {
        event.stopPropagation();
      }
      this.removeFromSelectedIds(id);
    };
    return { $implicit: id, id, onRemove, objectName: this.filterObjectName };
  }

  private removeFromSelectedIds(id: any): void {
    this.value = this.selectedIds.filter((selectedId) => selectedId !== id);
    this.changeDetectorRef.detectChanges();
  }
}
